import {Component, Input} from '@angular/core';
import {FormArray, FormControl, FormGroup, Validators} from "@angular/forms";
import {fileValidator} from "../validators/file-upload-validator";
import {baseFile} from "../../models/grantApplication/grantApplicationGetinfoResponse";
import {GrantApplicationService} from "../../services/grantApplication.service";
import {NgxSpinnerService} from "ngx-spinner";
import {BaseModel} from "../../models/BaseModel";
import {ApplicationFileInfo} from "../../models/grantApplication/grantApplicationResponse";
import Swal from "sweetalert2";

const DEFAULT_MAX_FILE_SIZE = 5; // Maximum file size in MB
const DEFAULT_ALLOWED_FILE_TYPES = [
  'image/jpeg',
  'image/png',
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/zip',
  '.jpg',
  '.jpeg',
  '.png',
  '.pdf',
  '.doc',
  '.docx',
  '.xlsx',
  '.xls',
  '.zip',
  'application/x-zip-compressed'
];

@Component({
  selector: 'app-upload-form',
  templateUrl: './upload-form.component.html',
  styleUrls: ['./upload-form.component.scss']
})
export class UploadFormComponent {
  @Input() data!: baseFile[];

  @Input() documentDatas!: FormArray;

  @Input() parentForm!: FormGroup;
  @Input() isReadOnly: boolean = false;

  constructor(private grantApplicationService: GrantApplicationService,
              private spinner: NgxSpinnerService,) {
  }



  onFileChange(event: any, index: number): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length) {
      const file = input.files[0];
      const fileName = file.name;
      const fileType = file.type;

      // Check if file type is allowed
      if (!DEFAULT_ALLOWED_FILE_TYPES.includes(fileType)) {
        Swal.fire({
          icon: 'error',
          title: 'Invalid File Type',
          text: `The selected file "${fileName}" is not allowed. Please upload JPEG, PDF, Word, Excel, or ZIP files only.`,
          confirmButtonText: 'OK'
        });
        input.value = ''; // Clear the input if the file is invalid
        return;
      }

      const isDuplicateInData = this.data?.some(existingFile => existingFile.name === fileName);

      if (isDuplicateInData) {
        Swal.fire({
          icon: 'warning',
          title: 'Duplicate File',
          text: `The file "${fileName}" already exists. Do you want to replace it?`,
          showCancelButton: true,
          confirmButtonText: 'Yes, Replace',
          cancelButtonText: 'No, Cancel'
        }).then(result => {
          if (result.isConfirmed) {
            this.updateFormArrayWithFile(file, index);
          } else {
            input.value = ''; // Clear the input if the user cancels replacing
          }
        });
      } else {
        this.updateFormArrayWithFile(file, index); // Add the file if it passes all validations
      }
    }
  }

  /**
   * Creates a form group for handling file uploads with validation.
   * The form group includes controls for the file to be uploaded and its name.
   *
   * @return {FormGroup} A form group containing the controls for file upload and its corresponding validations.
   */
  createFileUploadFormGroup(): FormGroup {
    return new FormGroup({
      file: new FormControl('', [
        Validators.required,
        fileValidator(DEFAULT_MAX_FILE_SIZE, DEFAULT_ALLOWED_FILE_TYPES)
      ]),
      name: new FormControl('', Validators.required)
    });
  }

  removeFile(index: number): void {
    this.documentDatas.removeAt(index);
  }

  addUploadFormControl(): void {
    this.documentDatas.push(this.createFileUploadFormGroup());
  }

  async downloadFile(fileId: string): Promise<void> {
    const DEFAULT_MIME_TYPE = 'application/octet-stream';
    const FALLBACK_EXTENSION = 'file';

    await this.spinner.show();

    return new Promise<void>((resolve, reject) => {
      this.grantApplicationService.downloadFile(fileId).subscribe((response: BaseModel<ApplicationFileInfo>) => {
          if (response?.result && response.item) {
            const fileInfo = response.item;
            const base64Data = fileInfo.documentBody ?? '';
            const mimeType = fileInfo.mimeType ?? DEFAULT_MIME_TYPE;
            const fileName = fileInfo.fileName || `${fileId}.${mimeType.split('/')[1] || FALLBACK_EXTENSION}`;

            const fileBlob = this.decodeBase64ToBlob(base64Data, mimeType);
            this.triggerFileDownload(fileBlob, fileName);
            this.spinner.hide();
            resolve();
          } else {
            console.error('File download failed.', response?.messages || 'Unknown error.');
            this.spinner.hide();
            reject(new Error('File download failed.'));
          }
        },
        error => this.handleDownloadError(error, reject)
      );
    });
  }

  private decodeBase64ToBlob(base64Data: string, mimeType: string): Blob {
    const byteCharacters = atob(base64Data);
    const byteNumbers = Array.from(byteCharacters, char => char.charCodeAt(0));
    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], {type: mimeType});
  }

  private triggerFileDownload(blob: Blob, fileName: string): void {
    const downloadUrl = window.URL.createObjectURL(blob);
    const downloadLink = document.createElement('a');
    downloadLink.href = downloadUrl;
    downloadLink.download = fileName;
    downloadLink.click();
    window.URL.revokeObjectURL(downloadUrl);
  }

  private handleDownloadError(error: any, reject: (reason?: any) => void): void {
    console.error('Error occurred while downloading file', error);
    this.spinner.hide();
    reject(error);
  }

  /**
   * Updates a specific FormArray entry at the given index with the provided file information.
   *
   * @param {File} file - The file object containing the file data to be updated in the FormArray.
   * @param {number} index - The index of the FormArray entry to update.
   * @return {void} This method does not return a value.
   */
  private updateFormArrayWithFile(file: File, index: number): void {
    this.documentDatas.at(index).patchValue({
      name: file.name,
      file: file
    });
  }

}
